import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import ModuleContext from 'CMFW/context/ModuleContext';
import { getCacheKey } from 'helpers/cache';
import { IRefIds } from 'constants/refs';
import { useCacheData } from 'hooks/useCacheData';
import KlinesModel from 'models/KlinesModel';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab-old/Autocomplete';
import Chip from '@material-ui/core/Chip';

type Props = {
  value: number[];
  onChange: (ids: number[]) => void;
  className?: string;
  label?: string;
};

const KLineSelectorList: React.FC<Props> = ({ value, onChange, className }) => {
  const moduleModel = React.useContext(ModuleContext) as any;

  const cacheKey = getCacheKey(IRefIds.klines, moduleModel.courseId);
  const dataModel = useMemo(() => new KlinesModel(moduleModel.courseId), [moduleModel.courseId]);
  const cacheSelectData = useCacheData(cacheKey, dataModel.get.bind(dataModel));

  const options = useMemo(
    () =>
      Array.isArray(cacheSelectData)
        ? cacheSelectData.map((k: any) => ({ value: k.id as number, label: String(k.title || `#${k.id}`) }))
        : ([] as Array<{ value: number; label: string }>),
    [cacheSelectData],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, selectedOptions: Array<{ value: number; label: string }>) => {
      const selectedValues = selectedOptions.map((opt) => opt.value);
      onChange(selectedValues);
    },
    [onChange],
  );

  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  return (
    <Wrapper className={className}>
      <AutoCompleteStyled
        multiple
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        getOptionLabel={(option) => option.label}
        getOptionSelected={(option, val) => option.value === val.value}
        filterSelectedOptions
        renderInput={(params) => <TextFieldStyled {...params} variant="outlined" label="Select K-Lines" size="small" />}
        renderTags={(vals: Array<{ value: number; label: string }>, getTagProps) =>
          vals.map((option, index) => (
            <ChipStyled key={option.value} label={option.label} {...getTagProps({ index })} size="small" />
          ))
        }
        disableCloseOnSelect
      />
    </Wrapper>
  );
};

export default KLineSelectorList;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const AutoCompleteStyled = styled(Autocomplete)`
  width: 100%;

  & .MuiInputBase-root {
    padding-top: 6px;
    padding-bottom: 6px;
  }

  & .MuiInputBase-input {
    height: auto;
    min-height: 24px;
  }

  & .MuiInputLabel-outlined.MuiInputLabel-marginDense {
    transform: translate(14px, 12px) scale(1);
  }

  & .MuiInputLabel-outlined.MuiInputLabel-shrink {
    transform: translate(14px, -6px) scale(0.75);
  }

  & .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] {
    padding-right: 65px;
  }

  & .MuiAutocomplete-tag {
    margin: 2px;
  }
`;

const TextFieldStyled = styled(TextField)`
  width: 100%;
`;

const ChipStyled = styled(Chip)`
  && {
    height: 24px;
    background: #f5f5f5;
    border: 1px solid #ddd;

    & .MuiChip-label {
      padding-left: 8px;
      padding-right: 8px;
      font-size: 13px;
    }

    & .MuiChip-deleteIcon {
      font-size: 18px;
      color: #666;

      &:hover {
        color: #000;
      }
    }
  }
`;
